import {
  LayoutDashboard, FolderKanban, PiggyBank, Users, FileBarChart, Send, RefreshCw,
  Settings, BarChart2, Layers, Briefcase, ClipboardList, CalendarDays, GitBranch,
  BookOpen, PieChart, Trophy, Cpu, HelpCircle, Monitor, FileSearch, MapPin,
  Lightbulb, Scale, Target, Clock, Brain, Sparkles, FileCheck,
  Network, BarChart3, Inbox, Globe2, MapPinned, ListChecks, ShieldAlert, Gauge,
} from "lucide-react";

// ── Single source of truth for platform navigation ──────────────────────────
// Used by the Sidebar (Layout.jsx) and the Command Palette (Ctrl+K).
// Items with a `module` property are gated by user.module_permissions.
//
// Iter311 · G02 — Agrupación según los "5 espacios" del informe de mejoras:
// Mi trabajo, Comercial, Ejecución, Dirección, Plataforma. Las rutas NO
// cambian (acceso antiguo se conserva); sólo se reorganiza a qué grupo
// pertenece cada acceso, para que la familia de conceptos sea coherente
// (p.ej. adjudicaciones/arranque pasan de "Comercial" a "Ejecución").

export const DASHBOARD_ITEM = { to: "/dashboard", icon: LayoutDashboard, label: "layout.dashboard" };
// Cockpit Ejecutivo · portada de 60s (roadmap 16/10). Justo tras Dashboard.
export const COCKPIT_ITEM = { to: "/cockpit", icon: Gauge, label: "Cockpit Ejecutivo" };
// Iter311 · G01 — "Mi trabajo": bandeja por perfil con vencimientos, aprobaciones,
// bloqueos y siguiente acción. Visible para todos los roles, justo tras Dashboard.
export const MI_TRABAJO_ITEM = { to: "/mi-trabajo", icon: ListChecks, label: "layout.miTrabajo" };

export const NAV_GROUPS = [
  {
    id: "comercial",
    label: "layout.sectionCommercial",
    items: [
      { to: "/licitaciones",        icon: Scale,      label: "layout.licitaciones" },
      { to: "/tender-intelligence", icon: Network,    label: "layout.tenderIntelligence" },
      { to: "/mercado-sectorial",   icon: Globe2,     label: "layout.mercadoSectorial" },
      { to: "/tender360",           icon: Target,     label: "layout.tender360" },
      { to: "/ofertas-comercial",   icon: FileSearch, label: "layout.ofertasComercial" },
      { to: "/clientes-360",        icon: Users,      label: "Clientes 360" },  // CRM-R06
      { to: "/ytd-board",           icon: BarChart3,  label: "YTD Board" },  // Iter230
      { to: "/business-review",     icon: FileBarChart, label: "Business Review" },  // Entrega D 12/10/2026
      { to: "/hit-rate-zonas",      icon: MapPinned,  label: "Resultados por territorio" },  // Iter282 (CCAA/geografía)
      { to: "/resultados-por-zona", icon: Layers,     label: "Resultados por zona comercial" },  // Z02 (organizativa)
      { to: "/offers",              icon: Briefcase,  label: "layout.offers" },
    ],
  },
  {
    id: "ejecucion",
    label: "layout.sectionExecution",
    items: [
      { to: "/adjudicaciones",   icon: Trophy,        label: "layout.adjudicaciones", module: "adjudicaciones" },
      { to: "/ora-zaragoza",     icon: MapPin,        label: "layout.oraZaragoza" },
      { to: "/projects",         icon: FolderKanban,  label: "layout.projects" },
      { to: "/horas",            icon: Clock,         label: "layout.horas" },
      { to: "/backlog",          icon: Layers,        label: "layout.backlog" },
      { to: "/sprint-dashboard", icon: GitBranch,     label: "layout.sprints" },
      { to: "/demanda",          icon: Inbox,         label: "layout.demanda", module: "demanda" },
      { to: "/checklists",       icon: ClipboardList, label: "layout.checklists" },
      { to: "/integration-it",   icon: FileCheck,     label: "layout.integrationIt" },
      { to: "/it-peticiones",    icon: Cpu,           label: "layout.itPeticiones", module: "it_peticiones" },
    ],
  },
  {
    id: "direccion",
    label: "layout.sectionDirection",
    items: [
      { to: "/executive",       icon: BarChart2,    label: "layout.executive" },
      { to: "/committee",       icon: PieChart,     label: "layout.committee" },
      { to: "/weekly-summary",  icon: CalendarDays, label: "layout.weeklySummary" },
      { to: "/resource-report", icon: FileBarChart, label: "layout.resourceReport" },
      { to: "/reports",         icon: FileBarChart, label: "layout.reports" },
      { to: "/resources",       icon: Users,        label: "layout.resources" },
      { to: "/assignments",     icon: Send,         label: "layout.assignments" },
      { to: "/budget",          icon: PiggyBank,    label: "layout.budget" },
    ],
  },
  {
    id: "conocimiento",
    label: "layout.sectionKnowledge",
    items: [
      { to: "/catalogo-aplicaciones", icon: Monitor,    label: "layout.appCatalog" },
      { to: "/catalogo-servicios",    icon: BookOpen,   label: "layout.catalogoServicios" },
      { to: "/innovacion",            icon: Lightbulb,  label: "layout.innovacion" },
      { to: "/poc-ai",                icon: Sparkles,   label: "layout.pocAi" },
      { to: "/manuales",              icon: HelpCircle, label: "layout.manuals" },
    ],
  },
];

// T09 (tanda integral 12/10/2026) · "Administración" agrupa herramientas de
// operación/calidad con los accesos admin-only que ya existían (antes
// "devops"/"calidad" vivían sueltos dentro de "plataforma", mezclados con
// accesos de consulta de cualquier rol — confuso para quien no administra).
export const ADMIN_ITEMS = [
  { to: "/devops",     icon: RefreshCw,   label: "layout.devops" },
  { to: "/calidad",    icon: ShieldAlert, label: "layout.incidenciasCalidad" },
  { to: "/zonas-admin", icon: MapPin,     label: "Zonas (catálogo)" },  // Z01
  { to: "/ai-history", icon: Brain,       label: "layout.aiHistory" },
  { to: "/admin",      icon: Settings,    label: "layout.admin" },
];

// Menú reducido para Responsables de Oferta
const OFFER_MANAGER_GROUPS = [
  {
    id: "comercial",
    label: "layout.sectionCommercial",
    items: [
      { to: "/licitaciones",      icon: Scale,      label: "layout.licitaciones" },
      { to: "/ofertas-comercial", icon: FileSearch, label: "layout.ofertasComercial" },
      { to: "/clientes-360",      icon: Users,      label: "Clientes 360" },  // CRM-R06
      { to: "/tender-intelligence", icon: Network,  label: "layout.tenderIntelligence" },
      { to: "/ytd-board",          icon: BarChart3, label: "YTD Board" },
      { to: "/business-review",   icon: FileBarChart, label: "Business Review" },  // Entrega D 12/10/2026
      { to: "/hit-rate-zonas",     icon: MapPinned, label: "Resultados por territorio" },  // Iter282 (CCAA/geografía)
      { to: "/resultados-por-zona", icon: Layers,   label: "Resultados por zona comercial" },  // Z02 (organizativa)
      { to: "/mercado-sectorial", icon: Globe2,     label: "layout.mercadoSectorial" },
      { to: "/tender360",         icon: Target,     label: "layout.tender360" },
      { to: "/offers",            icon: Briefcase,  label: "layout.offers" },
    ],
  },
  {
    id: "ejecucion",
    label: "layout.sectionExecution",
    items: [
      { to: "/horas",             icon: Clock,      label: "layout.horas" },
      { to: "/integration-it",    icon: FileCheck,  label: "layout.integrationIt" },
    ],
  },
  {
    id: "conocimiento",
    label: "layout.sectionKnowledge",
    items: [
      { to: "/catalogo-aplicaciones", icon: Monitor,    label: "layout.appCatalog" },
      { to: "/catalogo-servicios",    icon: BookOpen,   label: "layout.catalogoServicios" },
      { to: "/innovacion",            icon: Lightbulb,  label: "layout.innovacion" },
      { to: "/manuales",              icon: HelpCircle, label: "layout.manuals" },
    ],
  },
];

// Menú reducido para Gestores de Checklist
const CHECKLIST_MANAGER_GROUPS = [
  {
    id: "ejecucion",
    label: "layout.sectionExecution",
    items: [
      { to: "/integration-it", icon: FileCheck, label: "layout.integrationIt" },
    ],
  },
];

export function getVisibleGroups(user) {
  if (!user) return [];
  if (user.role === "checklist_manager") return CHECKLIST_MANAGER_GROUPS;
  if (user.role === "offer_manager") return OFFER_MANAGER_GROUPS;
  const hasModule = (m) => user.role === "admin" || (user.module_permissions || []).includes(m);
  return NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => !it.module || hasModule(it.module)) }))
    .filter((g) => g.items.length > 0);
}
